
<template>
  <div>
    <!-- Debug information (only in development) -->
    <ClientOnly>
      <div v-if="isDevelopment" class="bg-yellow-100 p-4 mb-4 text-xs">
        <h3 class="font-bold">Debug Info:</h3>
        <p>Route param: {{ target_specialty_str }}</p>
        <p>specialtyPending: {{ specialtyPending }}</p>
        <p>specialtyError: {{ specialtyError }}</p>
        <p>transformedSpecialtyData: {{ transformedSpecialtyData ? 'Yes' : 'No' }}</p>
        <p>highlightDoctors: {{ highlightDoctors ? highlightDoctors.length : 'No data' }}</p>
        <pre v-if="transformedSpecialtyData">{{ JSON.stringify(transformedSpecialtyData, null, 2) }}</pre>
      </div>
    </ClientOnly>

    <!-- Error boundary for critical failures -->
    <div v-if="specialtyError && !specialtyPending" class="bg-red-50 border border-red-200 rounded-lg p-6 m-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            专科信息加载失败
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p>抱歉，当前专科信息暂时无法显示。请稍后再试或联系客服。</p>
            <ClientOnly>
              <p v-if="isDevelopment" class="mt-1 text-xs">
                Error: {{ specialtyError }}
              </p>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="specialtyPending" class="flex items-center justify-center py-20">
      <div class="flex flex-col items-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <div class="text-gray-500 mt-4">加载专科信息中...</div>
      </div>
    </div>

    <!-- Main content with proper conditional rendering -->
    <div v-else-if="transformedSpecialtyData">
      <MedicalSpecialtyHero
        :specialty_data="transformedSpecialtyData"
        :doctors_data="highlightDoctors || []"
      />
    </div>

    <!-- Fallback content if no data -->
    <div v-else class="flex items-center justify-center py-20">
      <div class="text-center">
        <div class="text-red-500 text-lg font-medium">专科信息暂时不可用</div>
        <div class="text-gray-500 mt-2">请稍后再试或选择其他专科</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { computed } from 'vue'

const { setSEOMeta, setLLMOptimizedMeta, setHreflangAlternates } = useSEO()
// Emit hreflang alternates for all locales
try { setHreflangAlternates() } catch {}
const { fetchWithFallback, setFallbackSEO } = useSSRFallback()

// Development mode detection - safe for both SSR and client
const isDevelopment = computed(() => {
  try {
    return process.env.NODE_ENV === 'development'
  } catch {
    return false
  }
})

// 1) Get route param with SSR safety
const route = useRoute()
const target_specialty_str = computed(() => {
  // Handle both string and array params safely
  const param = route.params.str
  if (Array.isArray(param)) {
    return param[0] || 'unknown'
  }
  return param || 'unknown'
})

console.log('[Specialty Page] Target Specialty:', target_specialty_str.value)

// Ensure we have a valid specialty string before making API calls
if (!target_specialty_str.value || target_specialty_str.value === 'unknown') {
  console.error('[Specialty Page] Invalid specialty parameter:', route.params)
  // Preserve SEO with a bilingual fallback instead of throwing
  try {
    setSEOMeta({
      title: '纽约专科医生目录 - 北美好医生 | NYC Medical Specialties',
      description: '浏览纽约各类专科医生和诊所信息，包括常见疾病、诊疗服务与就医指引。Explore NYC medical specialties, common conditions, services, and how to choose the right doctor.',
      keywords: ['纽约专科医生', '华人医疗', '医生预约', '医疗保险', 'NYC specialists', 'Chinese doctors New York'],
      ogImage: '/HYS-logo-icon-06.png',
      locale: 'zh-Hans'
    })
  } catch (e) {
    console.error('[Specialty Page] Failed to set fallback SEO for invalid param:', e)
  }
}

// 2) Fetch specialty data with SEO-friendly fallback
const { data: specialtyData, pending: specialtyPending, error: specialtyError } = await fetchWithFallback(
  `specialty-${target_specialty_str.value}`,
  () => $fetch(`/api/specialty/getSpecialties`, {
    params: {
      target_specialty: target_specialty_str.value
    }
  }),
  {
    fallbackTitle: '专科医生介绍 - 北美好医生',
    fallbackDescription: '专业的医疗服务平台，为您提供优质的专科医生信息和预约服务',
    logError: true
  }
)

// 3) Fetch highlight-doctors data with fallback
const { data: highlightDoctors, pending: doctorsPending, error: doctorsError } = await fetchWithFallback(
  `doctors-${target_specialty_str.value}`,
  () => $fetch(`/api/doctor/getDoctorBySpecialty`, {
    params: {
      target_specialty: target_specialty_str.value
    }
  }),
  {
    fallbackTitle: '专科医生介绍 - 北美好医生',
    fallbackDescription: '专业的医疗服务平台，为您提供优质的专科医生信息和预约服务',
    logError: false // Less critical, don't log errors
  }
)

// 4) Transform API data to match component expectations
const transformedSpecialtyData = computed(() => {
  console.log('[Debug] Raw specialty data:', specialtyData.value)
  
  if (!specialtyData.value || typeof specialtyData.value !== 'object') {
    console.log('[Debug] No specialty data or invalid type')
    return null
  }

  const rawData = specialtyData.value
  console.log('[Debug] Raw data keys:', Object.keys(rawData))
  
  // Transform the API response to match what the component expects
  const transformed = {
    cn_name: rawData.meta_specialty_chinese_name || rawData.cn_name || '专科',
    en_name: rawData.meta_specialty_english_name || rawData.en_name || 'Specialty',
    cn_alts: rawData.meta_specialty_chinese_alternative_names || rawData.cn_alts || [],
    en_alts: rawData.meta_specialty_english_alternative_names || rawData.en_alts || [],
    short_brief: rawData.specialty_short_brief || rawData.short_brief || '',
    detailed_content: rawData.specality_detailed_content || rawData.detailed_content || '',
    common_illnesses: rawData.specialty_common_illness || rawData.common_illnesses || [],
    common_services: rawData.specialty_common_services || rawData.common_services || []
  }
  
  console.log('[Debug] Transformed data:', transformed)
  return transformed
})

// 5) Handle errors and set up SEO with graceful fallbacks
try {
  // Check for critical API errors
  if (specialtyError.value && process.server) {
    // On server, still provide SEO but with fallback content
    setFallbackSEO({
      title: '专科医生介绍 - 北美好医生',
      description: '寻找专科医生？北美好医生为您提供权威的医生信息、诊所地址、预约电话等详细资料。华人患者首选医疗服务平台。',
      keywords: ['专科医生', '纽约医生', '华人医疗', '医生预约', '医疗保险']
    })
    
    console.warn('[Specialty Page] Using fallback SEO due to API error')
  } else if (specialtyData.value && typeof specialtyData.value === 'object') {
    // Enhanced SEO for specialty pages - with null safety and bilingual optimization
    const specialty = specialtyData.value
    
    // Names in both languages
    const cnName = specialty.meta_specialty_chinese_name || specialty.cn_name || '专科'
    const enName = specialty.meta_specialty_english_name || specialty.en_name || 'Specialty'
    
    // Build specialty-focused keywords based on the medical field (Chinese)
    const zhKeywords = [
      cnName,
      `${cnName}医生`,
      `${cnName}专科`,
      `${cnName}治疗`,
      `${cnName}咨询`,
      '纽约专科医生',
      '华人医疗',
      '医生预约',
      '专科诊所',
      '医疗保险'
    ]

    // English keywords
    const enKeywords = [
      enName,
      `${enName} doctor`,
      `${enName} specialist`,
      `${enName} treatment`,
      `${enName} consultation`,
      `New York ${enName} doctor`,
      `NY ${enName} doctor`,
      `Chinese ${enName} doctor`,
      'NYC doctors',
      'New York specialists'
    ].filter(Boolean)

    // Create specialty-focused description highlighting medical services (bilingual)
    let enhancedDescriptionZh = `在纽约寻找${cnName}(${enName})专科医生？北美好医生为您提供专业的${cnName}医生信息，包括常见疾病治疗、专科服务介绍、医生资历和诊所预约信息。华人患者信赖的医疗服务平台。`
    let enhancedDescriptionEn = `Find ${enName} specialists in New York: common conditions, services, doctor profiles and booking guidance on HaoYiSheng.`
    let enhancedDescription = `${enhancedDescriptionZh} ${enhancedDescriptionEn}`
    
    // Aggregate keywords with conditions and services
    let enhancedKeywords = [...zhKeywords, ...enKeywords]

    if (specialty.specialty_common_illness) {
      const conditionKeywords = specialty.specialty_common_illness
        .slice(0, 5)
        .map(illness => illness.illness_name)
        .filter(name => name && name.length > 0)
      enhancedKeywords = [...enhancedKeywords, ...conditionKeywords]
    }
    
    if (specialty.specialty_common_services) {
      const serviceKeywords = specialty.specialty_common_services
        .slice(0, 3)
        .map(service => service.service_name)
        .filter(name => name && name.length > 0)
      enhancedKeywords = [...enhancedKeywords, ...serviceKeywords]
    }

    // Deduplicate and cap keyword length
    enhancedKeywords = Array.from(new Set(enhancedKeywords)).slice(0, 20)

    const metaTitle = `纽约${cnName}专科医生${enName ? ` (${enName})` : ''} | ${cnName}医疗服务指南`

    // Set comprehensive SEO meta focused on specialty medical services
    setSEOMeta({
      title: metaTitle,
      description: enhancedDescription,
      keywords: enhancedKeywords, // Already deduped and capped
      ogImage: '/HYS-logo-icon-06.png',
      locale: 'zh-Hans'
    })

    // Add structured data for specialty landing page
    try {
      const conditionThings = (specialty.specialty_common_illness || [])
        .slice(0, 8)
        .map(illness => ({ '@type': 'MedicalCondition', name: illness.illness_name }))
        .filter(item => item.name && item.name.length > 0)
      const serviceThings = (specialty.specialty_common_services || [])
        .slice(0, 8)
        .map(service => ({ '@type': 'Service', serviceType: service.service_name, name: service.service_name }))
        .filter(item => item.name && item.name.length > 0)

      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'MedicalWebPage',
        name: metaTitle,
        inLanguage: 'zh-Hans',
        about: {
          '@type': 'MedicalSpecialty',
          name: enName
        },
        description: enhancedDescription,
        mentions: [...conditionThings, ...serviceThings]
      }
      useHead({
        script: [
          { type: 'application/ld+json', innerHTML: JSON.stringify(structuredData) }
        ]
      })
    } catch (sdError) {
      console.warn('[Specialty Page] Failed to set structured data:', sdError)
    }

    // Set LLM-optimized metadata for specialty medical information
    setLLMOptimizedMeta({
      contentType: 'medical-specialty-directory', 
      primaryTopic: `${cnName}专科医疗服务`,
      keyTerms: enhancedKeywords.slice(0, 12),
      contentSummary: `${cnName}专科医疗服务完整指南，包括专科介绍、常见疾病、治疗服务、医生选择和预约指导`,
      medicalSpecialties: [cnName, enName],
      targetAudience: `需要${cnName}专科医疗服务的患者和家属`,
      // Include specialty-specific medical information
      medicalConditions: specialty.specialty_common_illness?.map(illness => illness.illness_name) || [],
      medicalServices: specialty.specialty_common_services?.map(service => service.service_name) || [],
      patientGuidance: `专业的${cnName}医疗服务指导和医生推荐`
    })

    // Breadcrumbs JSON-LD for specialty page
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haodrs.com/' },
        { '@type': 'ListItem', position: 2, name: `${cnName}`, item: `https://haodrs.com${route.path}` }
      ]
    }
    useHead({ script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(breadcrumbLd) }] })
    
    console.log('[Specialty Page] SEO configured for:', cnName)
  } else {
    // Client-side fallback SEO when data is missing but no server error
    try {
      setSEOMeta({
        title: '纽约专科医生目录 - 北美好医生 | NYC Medical Specialties',
        description: '浏览纽约各类专科医生和诊所信息，包括常见疾病、诊疗服务与就医指引。Explore NYC medical specialties, common conditions, services, and how to choose the right doctor.',
        keywords: ['纽约专科医生', '华人医疗', '医生预约', '医疗保险', 'NYC specialists', 'Chinese doctors New York'],
        ogImage: '/HYS-logo-icon-06.png',
        locale: 'zh-Hans'
      })
    } catch (e) {
      console.error('[Specialty Page] Failed to set client fallback SEO:', e)
    }
  }

  // Handle doctor data errors gracefully
  if (doctorsError.value) {
    console.warn('[Specialty Page] Doctors API Error (non-critical):', doctorsError.value)
  }

} catch (error) {
  console.error('[Specialty Page] Failed to setup page:', error)
  
  // Don't throw on client side to allow SPA fallback
  if (process.server) {
    throw createError({
      statusCode: 500,
      statusMessage: '页面加载失败'
    })
  }
}
</script>
